import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { intelligenceApi } from '../lib/adapter';
import type { AIConversation, AICustomerProfile } from '../types/contracts';
import { IntelligenceShell } from '../components/IntelligenceShell';
import { ConversationList } from '../components/ConversationList';
import { ConversationView } from '../components/ConversationView';
import { CustomerIntelligencePanel } from '../components/CustomerIntelligencePanel';
import { EmptyState, ErrorState, Skeleton } from '../components/states';

export default function ConversationsPage() {
  const { id } = useParams();
  return id ? <ConversationDetail id={id} /> : <ConversationIndex />;
}

function ConversationIndex() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<AIConversation[]>([]);
  const [query, setQuery] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    const r = await intelligenceApi.listConversations();
    setLoading(false);
    if (!r.ok) { setError('message' in r ? r.message : 'حدث خطأ ما'); return; }
    setData(r.data);
  };
  useEffect(() => { void load(); }, []);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? data.filter((c) => [c.customer_name, c.business_name, c.last_message].join(' ').toLowerCase().includes(q))
    : data;

  return (
    <IntelligenceShell onSearch={setQuery} searchValue={query}>
      {loading ? <Skeleton rows={5} /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && filtered.length === 0 ? (
        <EmptyState title="لا توجد محادثات" body="لا توجد محادثات نشطة تطابق بحثك." />
      ) : null}
      {!loading && !error && filtered.length > 0 ? <ConversationList conversations={filtered} title="المحادثات" /> : null}
    </IntelligenceShell>
  );
}

function ConversationDetail({ id }: { id: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [conversation, setConversation] = useState<AIConversation | null>(null);
  const [profile, setProfile] = useState<AICustomerProfile | null>(null);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await intelligenceApi.getConversation(id);
      if (!r.ok) { setLoading(false); setError('message' in r ? r.message : 'حدث خطأ ما'); return; }
      setConversation(r.data);
      if (r.data.customer_id) {
        const p = await intelligenceApi.getCustomerProfile(r.data.customer_id);
        if (p.ok) setProfile(p.data);
      }
      setLoading(false);
    } catch (e) {
      setLoading(false);
      setError(e instanceof Error ? e.message : 'تعذر فتح المحادثة');
    }
  };
  useEffect(() => { void load(); }, [id]);

  return (
    <IntelligenceShell>
      {loading ? <Skeleton rows={8} /> : null}
      {error ? <ErrorState message={error} onRetry={() => void load()} /> : null}
      {!loading && !error && conversation ? (
        <div className="grid lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2">
            <ConversationView conversation={conversation} />
          </div>
          {profile ? <CustomerIntelligencePanel profile={profile} /> : <Skeleton rows={5} />}
        </div>
      ) : null}
    </IntelligenceShell>
  );
}
